import {
  createAgentSession,
  getAgentSession,
  getAgentSessionControls,
  updateAgentSessionStep,
} from "@/modules/sessions/requests";
import {
  AgentSession,
  AgentSessionControl,
  AgentSessionStatus,
  AgentSessionStep,
  AgentSessionStepType,
  AgentSessionType,
  CommonRagCreateRequest,
  PolicyAgentSession,
} from "@/modules/sessions/types";

import { userStateSelector } from "@/shared/states/user";
import { AiResponseType } from "@/shared/types/user";
import { NavigateFunction } from "react-router-dom";
import { emitRagCreate, getRAGExcelFromJSON } from "../requests";
import { getAgentData, getAgentStateActions } from "../states";
import {
  AGENT_TYPES,
  AgentData,
  AgentSourceFile,
  PolicyManagementTypes,
} from "../types/index.ts";
import { getResponseTypes } from "../utils/get-response-type";
import { handleSocketResponse } from "../utils/socket-response";
import { getFileNameFromUrl } from "../utils/get-filename-from-url";
import { AutoSaveFunctionArgs } from "../utils/autosave";
import { useAgentSocket } from "../utils/agent-socket.ts";
import { addNotification } from "@/shared/states/notification.ts";
import { getFileNameForDownloadReport } from "../utils/downloadReport.ts";
import { AGENT_ROUTES, ROUTES } from "@/shared/constants/routes.ts";
import { getSignedUrl } from "@/shared/requests/get-signed-url.ts";

interface ProcessDataForPolicyManagementArgs {
  name: string;
  documentation_name: string;
  additional_instructions: string;
  documentation_type: string;
  documentation_action: string;
  compliance_frameworks: string[];
  update_existing_document_url?: AgentSourceFile;
  derive_from_documents_urls: AgentSourceFile[];
  sourceFiles: AgentSourceFile[];
  navigate: NavigateFunction;
}

export const processDataForPolicyManagement = async ({
  name,
  documentation_name,
  additional_instructions,
  documentation_type, 
  documentation_action,
  compliance_frameworks,
  update_existing_document_url,
  derive_from_documents_urls,
  sourceFiles,
  navigate,
}: ProcessDataForPolicyManagementArgs) => {
  
  const selectedResponse =
    userStateSelector.getState().aiResponseType === AiResponseType.NONE
      ? AiResponseType.LITE
      : userStateSelector.getState().aiResponseType;

  const { response_mode, response_quality } = getResponseTypes(selectedResponse);

  // Prepare source URLs from knowledge base files
  const source_urls = sourceFiles?.map((file) => file.url) || [];
  
  // Prepare derive document URLs
  const derive_urls = derive_from_documents_urls?.map((file) => file.url) || [];
  
  // Prepare update existing document URL (single file)
  const update_url = update_existing_document_url?.url || "";

  const policyManagement: CommonRagCreateRequest & {
    documentation_name: string;
    documentation_type: string;
    documentation_action: string;
    additional_instructions: string;
    update_existing_document_url?: string;
    derive_from_documents_urls?: string[];
    compliance_frameworks: string[];
  } = {
    doc_type: "json",
    response_quality,
    source_urls,
    documentation_name,
    documentation_type,
    documentation_action,
    additional_instructions,
    update_existing_document_url: update_url,
    derive_from_documents_urls: derive_urls.length > 0 ? derive_urls : [],
    compliance_frameworks,
  };
  

  // Create the session
  const { data } = await createAgentSession({
    name: name ?? "Policy Management",
    type: AgentSessionType.POLICY_MANAGEMENT,
    customize: false, // Default value, can be made configurable later
    policy_management: policyManagement,
  });

  const session: PolicyAgentSession = data.session as PolicyAgentSession;
  const steps: AgentSessionStep[] = data.steps;

  if (!session || !session.id) {
    throw new Error("An error occurred while creating the session");
  }
 

//   // Set up step data
  const stepData = steps?.reduce((acc, step) => {
    if ([AgentSessionStepType.LOAD_TEMPLATE].includes(step.type)) {
      step.status = AgentSessionStatus.COMPLETE;
    }
    if (step.type === AgentSessionStepType.POLICY_MANAGEMENT) {
      step.status = AgentSessionStatus.IN_PROGRESS;
    }
    acc.push(step);
    return acc;
  }, [] as AgentSessionStep[]);

//   // Create agent data
  const agentData: AgentData<AGENT_TYPES.POLICY_MANAGEMENT, PolicyManagementTypes> = {
    agentType: AGENT_TYPES.POLICY_MANAGEMENT,
    subType: documentation_type as PolicyManagementTypes,
    sessionData: session,
    stepData: stepData || [],
    responseQuality: selectedResponse,
    mainData: {
      sourceFilesUrls: sourceFiles?.length ? new Map(sourceFiles.map((file) => [file.fileName, file])) : new Map(),
      approvedIds: [],
      editedIds: [],
    },
  };

  const agent_session_step_id =
    steps?.find((step) => step.type === AgentSessionStepType.POLICY_MANAGEMENT)?.id ??
    steps?.find((step) => step.type === AgentSessionStepType.EDIT_RESPONSE)?.id ??
    "";

  const { setAgentData } = getAgentStateActions();
  setAgentData(session.id, agentData);

//   // Emit the policy creation request
  await emitRagCreate(
    {
      agent_session_id: session.id,
      agent_session_step_id,
      rag_input: policyManagement,
      response_mode,
      task: "policy_management" as any, // Type assertion to work around missing type
    },
    (response: any) => {
      handleSocketResponse(response, navigate);
    },
  );

//   // Navigate to the agent session page
  navigate({
    pathname: `/agent/${session.id}/`,
  });
};

export const processFileForPolicyManagement = async (sessionId: string) => {
  try {
    const sessionControls = await getAgentSessionControls(sessionId);
    const policyData = sessionControls.data?.[0]?.policy_management || null;
    
   
    
    if (policyData && policyData.response) {
      // Store the policy content in the agent state for the Editor to use
      const { setAgentData } = getAgentStateActions();
      
      // Get current agent data
      const currentData = getAgentData(sessionId);
      if (currentData) {
        // Update step status from POLICY_MANAGEMENT IN_PROGRESS to EDIT_RESPONSE IN_PROGRESS
        const updatedStepData = currentData.stepData.map((step) => {
          if (step.type === AgentSessionStepType.POLICY_MANAGEMENT) {
            return { ...step, status: AgentSessionStatus.COMPLETE };
          }
          if (step.type === AgentSessionStepType.EDIT_RESPONSE) {
            return { ...step, status: AgentSessionStatus.IN_PROGRESS };
          }
          return step;
        });

        const updatedData = {
          ...currentData,
          stepData: updatedStepData,
          mainData: {
            ...currentData.mainData,
            policyContent: policyData.response,
            policyId: policyData.id,
            sources: policyData.sources,
            isApproved: policyData.approved,
            isEdited: policyData.edited,
            controlId: policyData.control_id,
            confidence: policyData.confidence,
            lastUpdate: {
              timestamp: Date.now(),
              isRemoteUpdate: true,
              isAiGenerated: true, // Initial AI-generated policy content should be animated
            },
          }
        };
        setAgentData(sessionId, updatedData);
      }
    }
  } catch (error) {
    console.error("Error processing policy management file:", error);
  }
};

export const handlePolicyManagementAutoSave = async ({ agentId, markAsComplete }: AutoSaveFunctionArgs) => {
  const agentData = getAgentData<AGENT_TYPES.POLICY_MANAGEMENT, PolicyManagementTypes>(agentId);
  
  if (!agentData) {
    throw new Error("An error occurred while getting agent data");
  }

  const { stepData, mainData } = agentData;
  
  // CRITICAL: Check if this is a recent remote update to prevent race conditions
  const lastUpdate = mainData.lastUpdate;
  if (lastUpdate?.isRemoteUpdate && Date.now() - lastUpdate.timestamp < 2000) {
    
    return;
  }

  const { policyContent, policyId } = mainData;

  if (!policyContent) {
    console.warn("No policy content to save");
    return;
  }


  const editStepData = stepData?.find((step) => step.type === AgentSessionStepType.EDIT_RESPONSE);

  if (!editStepData) {
    throw new Error("Edit response step not found for policy management");
  }

  // Create the policy data object to be saved
  const policyData = {
    id: policyId,
    response: policyContent,
    sources: mainData.sources || [],
    approved: mainData.isApproved || false,
    edited: true, // Mark as edited when autosaving
  };

  const controlId = mainData.controlId;
  const { emitControlEdit } = useAgentSocket();
  
  try {
    const editedField = agentData?.mainData.lastEditedField || "response";
    // For policy management, we don't need file URLs - just save the content directly
    await emitControlEdit("", policyData, controlId, agentId, editStepData.id, editedField);
  } catch (error) {
    console.error("Error in policy management emitControlEdit:", error);
    throw error;
  }
};

export const getPolicyReportFromApi = async (sessionId: string) => {
  try {
    const agentData = getAgentData<AGENT_TYPES.POLICY_MANAGEMENT, PolicyManagementTypes>(sessionId);
    if (!agentData) {
      throw new Error("An error occurred");
    }
    const { responseQuality, sessionData, stepData } = agentData;

    addNotification({
      type: "success",
      title: "Report Download in Progress...",
      message: "If the download does not begin within 15 seconds, please click the 'Download' button",
    });

    const data = await getRAGExcelFromJSON({
      task: "policy_management",
      output_format: "pdf",
      response_quality: responseQuality ?? AiResponseType.LITE,
      agent_session_id: sessionId,
    });

    const { setFinalFileUrl, updateAgentStepData } = getAgentStateActions();

    setFinalFileUrl(sessionId, data.data.file_url);

    // Direct download using the file URL
    fetch(data.data.file_url)
    .then(response => response.blob())
    .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = getFileNameForDownloadReport(sessionData);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl); // Cleanup
    })

    const downloadStepData = stepData?.find((step) => step.type === AgentSessionStepType.PREPARE_REPORT);
    const editResponseStep = stepData?.find((step) => step.type === AgentSessionStepType.EDIT_RESPONSE);
    const updatedStepData = stepData.map((step) => {
      if (step.id === editResponseStep?.id) {
        return { ...step, status: AgentSessionStatus.COMPLETE };
      }
      if (step.id === downloadStepData?.id) {
        return {
          ...step,
          data: {
            url: data.data.file_url,
          },
        };
      }
      return step;
    });

    if (downloadStepData?.id) {
      await updateAgentSessionStep({
        ...downloadStepData,
        data: {
          url: data.data.file_url,
        },
      });
    }

    updateAgentStepData(sessionId, updatedStepData as AgentSessionStep[]);
  } catch (error) {
    console.error(error);
    addNotification({
      type: "error",
      message: "Error in downloading the file",
      title: "Error",
    });
  }
};

export const handleEndPolicySession = async (sessionId: string, navigate: NavigateFunction) => {
  const { setFinalFileUrl, updateAgentStepData } = getAgentStateActions();
  try {
    const agentData = getAgentData<AGENT_TYPES.POLICY_MANAGEMENT, PolicyManagementTypes>(sessionId);

    if (!agentData) {
      throw new Error("An error occurred");
    }

    const { stepData } = agentData;

    const editResponseStep = stepData?.find((step) => step.type === AgentSessionStepType.EDIT_RESPONSE);

    //if user directly clicks on end session without download report
    if (editResponseStep?.status !== AgentSessionStatus.COMPLETE) {
      const signedUrl = await getSignedUrl({
        file_names: ["updated_policy.json"],
        max_age: 86400,
      });

      // Process the policy file first
      await processFileForPolicyManagement(sessionId);

      const data = await getRAGExcelFromJSON({
        task: "policy_management",
        output_format: "pdf",
        response_quality: agentData.responseQuality ?? AiResponseType.LITE,
        agent_session_id: sessionId,
      });
      setFinalFileUrl(sessionId, data.data.file_url);

      const downloadStepData = stepData?.find((step) => step.type === AgentSessionStepType.PREPARE_REPORT);

      const updatedStepData = stepData.map((step) => {
        if (step.id === editResponseStep?.id) {
          return {
            ...step,
            status: AgentSessionStatus.COMPLETE,
            data: {
              url: signedUrl[0], // JSON URL for future processing
            },
          };
        }
        if (step.id === downloadStepData?.id) {
          return {
            ...step,
            data: {
              url: data.data.file_url,
            },
            status: AgentSessionStatus.COMPLETE,
          };
        }
        return step;
      });

      if (downloadStepData?.id) {
        await updateAgentSessionStep({
          ...downloadStepData,
          data: {
            url: data.data.file_url,
          },
          status: AgentSessionStatus.COMPLETE,
        });
      }
      updateAgentStepData(sessionId, updatedStepData as AgentSessionStep[]);
      const successPath = `/${ROUTES.AGENT}/${AGENT_ROUTES.SUCCESS}/${sessionId}`;
      navigate(successPath);
    } else {
      const prepareReportStep = stepData?.find((step) => step.type === AgentSessionStepType.PREPARE_REPORT);

      const updatedStepData = stepData.map((step) => {
        if (step.id === prepareReportStep?.id) {
          return { ...step, status: AgentSessionStatus.COMPLETE };
        }
        return step;
      });

      if (prepareReportStep?.id) {
        await updateAgentSessionStep({
          ...prepareReportStep,
          status: AgentSessionStatus.COMPLETE,
        });
      }
      await new Promise<void>((resolve) => {
        updateAgentStepData(sessionId, updatedStepData as AgentSessionStep[]);
        setTimeout(resolve, 0);
      });
      const successPath = `/${ROUTES.AGENT}/${AGENT_ROUTES.SUCCESS}/${sessionId}`;
      navigate(successPath);
    }
  } catch (error) {
    console.error(error);
    addNotification({
      type: "error",
      message: "Error in ending the session",
      title: "Error",
    });
  }
};